import React, { useState } from "react";
import { api, LOGO_URL } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Mail, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

const SOCIALS = [
  {
    key: "instagram",
    label: "Instagram",
    handle: "@highkeyllc_",
    href: "https://www.instagram.com/highkeyllc_/",
    avatar: LOGO_URL,
    cta: "Follow",
    Icon: InstagramIcon,
    accent: "from-[#fdc468] via-[#df4996] to-[#7c3aed]",
  },
  {
    key: "whatsapp",
    label: "WhatsApp Channel",
    handle: "High Key updates",
    href: "https://whatsapp.com/channel/0029VbAC5IgHAdNS8CcoCe3s",
    avatar: LOGO_URL,
    cta: "Join channel",
    Icon: WhatsAppIcon,
    accent: "from-[#25D366] to-[#128C7E]",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll get back.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Failed. Try again.");
    }
    setBusy(false);
  };

  return (
    <div className="pt-24 md:pt-28" data-testid="contact-page">
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-3xl">
          <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)] mb-6">/ WHERE TO REACH US</p>
          <h1 className="font-display text-6xl md:text-8xl font-bold uppercase tracking-tighter leading-[0.9]">
            Come find <span className="italic text-[var(--hk-purple-text)]">us</span>.
          </h1>
          <p className="mt-6 text-gray-400 text-lg max-w-2xl">
            DM us, join the channel, or pull up. All roads in one place.
          </p>
        </motion.div>
      </section>

      {/* SOCIAL CARDS */}
      <section className="px-6 md:px-12 lg:px-24 pb-12 md:pb-16">
        <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-purple-text)] mb-6">/ SOCIAL</p>
        <div className="grid md:grid-cols-2 gap-5">
          {SOCIALS.map(({ key, label, handle, href, avatar, cta, Icon, accent }, i) => (
            <motion.a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-${key}`}
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="group relative border border-white/10 hover:border-[var(--hk-purple)]/60 bg-gradient-to-br from-white/[0.03] to-transparent p-5 md:p-6 transition-all hover:-translate-y-1"
            >
              <div className="flex items-center gap-4 md:gap-5">
                <div className="relative flex-shrink-0">
                  <div className={`absolute -inset-0.5 rounded-full bg-gradient-to-tr ${accent} opacity-70 blur-[2px] group-hover:opacity-100 transition-opacity`} />
                  <img
                    src={avatar}
                    alt={label}
                    className="relative w-16 h-16 md:w-20 md:h-20 rounded-full object-cover bg-black border-2 border-black"
                  />
                  <span className="absolute -bottom-1 -right-1 w-7 h-7 md:w-8 md:h-8 rounded-full bg-black border-2 border-[var(--hk-bg)] flex items-center justify-center">
                    <Icon size={14} />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono-alt tracking-[0.3em] text-gray-500 uppercase">
                    / {label}
                  </p>
                  <p className="font-display text-2xl md:text-3xl uppercase tracking-tight text-white truncate">
                    {handle}
                  </p>
                  <span className="mt-1 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[var(--hk-orange)] group-hover:gap-2 transition-all">
                    {cta} <ExternalLink size={12} />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* CONTACT INFO TILES */}
      <section className="px-6 md:px-12 lg:px-24 pb-12 md:pb-16 grid md:grid-cols-2 gap-5">
        {[
          { Icon: MapPin, label: "Pull up", lines: ["NYC — by appointment only", "DM for appointments"] },
          { Icon: Mail, label: "Email", lines: ["HighKeyNyc420@gmail.com"] },
        ].map(({ Icon, label, lines }) => (
          <div key={label} className="border border-white/10 hover:border-[var(--hk-purple)]/60 transition-colors p-6 md:p-8">
            <Icon size={22} className="text-[var(--hk-orange)] mb-4" />
            <p className="font-mono-alt text-[10px] tracking-[0.4em] text-gray-500 uppercase mb-2">/ {label}</p>
            {lines.map((l) => (
              <p key={l} className="font-display text-xl uppercase tracking-tight text-white">{l}</p>
            ))}
          </div>
        ))}
      </section>

      {/* CONTACT FORM */}
      <section className="px-6 md:px-12 lg:px-24 pb-32">
        <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter mb-10">Drop us a line</h2>
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-6 max-w-3xl">
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Name</label>
            <input
              data-testid="contact-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Email</label>
            <input
              data-testid="contact-email"
              required type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Message</label>
            <textarea
              data-testid="contact-message"
              required rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-transparent border border-white/20 focus:border-[var(--hk-purple)] outline-none p-3"
            />
          </div>
          <button
            data-testid="contact-submit"
            disabled={busy}
            className="md:col-span-2 w-fit bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-8 py-4 rounded-sm hover:bg-[var(--hk-orange-hover)] transition-colors disabled:opacity-60"
          >
            {busy ? "..." : "Send"}
          </button>
        </form>
      </section>
    </div>
  );
}

function InstagramIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="igGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FDC468" />
          <stop offset="50%" stopColor="#DF4996" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#igGrad)" />
      <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="1.7" fill="none" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="white" />
    </svg>
  );
}

function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="5" fill="#25D366" />
      <path
        d="M16.5 13.5c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.5.1-.2.2-.6.7-.7.9-.1.1-.3.2-.5.1-.2-.1-1-.4-1.9-1.2-.7-.6-1.2-1.4-1.3-1.6-.1-.2 0-.3.1-.4.1-.1.2-.3.3-.4.1-.1.1-.2.2-.4.1-.1.1-.3 0-.4 0-.1-.5-1.2-.7-1.7-.2-.4-.3-.4-.5-.4h-.4c-.1 0-.4.1-.6.3-.2.2-.8.7-.8 1.8s.8 2.1.9 2.2c.1.1 1.6 2.5 4 3.5.6.3 1 .4 1.4.5.6.2 1.1.2 1.5.1.5-.1 1.4-.6 1.6-1.1.2-.5.2-1 .2-1.1-.1-.1-.2-.2-.4-.3z"
        fill="white"
      />
    </svg>
  );
}
